import React from 'react';

const SummaryDisplay = ({ summary }) => {
  const preprocessSection = (sectionString) => {
    // Handle both escaped and actual newlines
    sectionString = sectionString.replace(/\\n/g, '\n');
    
    // Split by lines and process
    const lines = sectionString.split('\n');
    const processedLines = [];
    let inBulletHeaderContext = false;
    let inStandaloneHeaderContext = false; // Track if we're after a standalone header
    let isInExecutiveSummary = false;
    let isInSentimentOverview = false;
    let inCodeBlock = false;
    let codeBlockContent = [];
    let inTable = false;
    let tableRows = [];
    let tableHeaders = [];
    
    for (let line of lines) {
      const originalLine = line;
      line = line.trim();
      
      // Handle code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          // End of code block - detect structured data (tree diagrams, key-value with arrows)
          const isStructuredData = codeBlockContent.some(l =>
            l.includes('├─') || l.includes('└─') || l.includes('│') || /[↑↓]/.test(l)
          );
          const blockType = isStructuredData ? 'structured-data' : 'code-block';
          processedLines.push({ type: blockType, content: codeBlockContent.join('\n') });
          codeBlockContent = [];
          inCodeBlock = false;
        } else {
          // Start of code block
          inCodeBlock = true;
          codeBlockContent = [];
        }
        continue;
      }
      
      if (inCodeBlock) {
        codeBlockContent.push(originalLine);
        continue;
      }
      
      // Handle table rows (detect by pipe separators)
      if (line.includes('|') && line.split('|').length >= 3) {
        const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
        
        // Check if this is a table separator row (e.g., |---|---|)
        const isSeparator = cells.every(cell => /^[-:]+$/.test(cell));
        
        if (isSeparator) {
          // Mark that we have headers
          if (!inTable && tableRows.length > 0) {
            tableHeaders = tableRows[0];
            tableRows = [];
            inTable = true;
          }
          continue;
        }
        
        if (!inTable && tableRows.length === 0) {
          // First row - potential header
          tableRows.push(cells);
          continue;
        } else if (inTable || tableRows.length > 0) {
          // Table data row
          tableRows.push(cells);
          continue;
        }
      } else if (inTable || tableRows.length > 0) {
        // End of table - process it
        if (tableHeaders.length > 0 && tableRows.length > 0) {
          processedLines.push({ type: 'table', headers: tableHeaders, rows: tableRows });
        } else if (tableRows.length > 0) {
          // No headers, treat first row as header
          processedLines.push({ type: 'table', headers: tableRows[0], rows: tableRows.slice(1) });
        }
        inTable = false;
        tableRows = [];
        tableHeaders = [];
        // Continue processing current line
      }
      
      // Skip empty lines
      if (line === '') continue;
      
      // Check if ENTIRE line content is wrapped in bold (not just parts of it)
      // For bullets/numbered lists, check content after the prefix
      let isEntireLineBold = false;
      if (line.includes('**')) {
        let contentToCheck = line;
        // Remove list prefixes to check the actual content
        if (line.startsWith('- ')) {
          contentToCheck = line.substring(2).trim();
        } else if (/^\d+\.\s/.test(line)) {
          contentToCheck = line.replace(/^\d+\.\s/, '').trim();
        }
        // Check if entire content is wrapped: starts and ends with ** and has exactly one pair
        if (contentToCheck.startsWith('**') && contentToCheck.endsWith('**')) {
          const withoutOuter = contentToCheck.substring(2, contentToCheck.length - 2);
          isEntireLineBold = !withoutOuter.includes('**');
        }
      }
      
      // Check if line has bold markers before removing them
      const hasBoldMarkers = line.includes('**');
      
      // Remove asterisks used for bold in markdown
      line = line.replace(/\*\*/g, '');
      
      // Main title (# header)
      if (line.startsWith('# ')) {
        processedLines.push({ type: 'main-title', content: line.substring(2) });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
        isInExecutiveSummary = false;
        isInSentimentOverview = false;
      }
      // Section headers (## headers)
      else if (line.startsWith('## ')) {
        processedLines.push({ type: 'section-header', content: line.substring(3) });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
        isInExecutiveSummary = false;
        isInSentimentOverview = false;
      }
      // Subsection headers (### headers)
      else if (line.startsWith('### ')) {
        processedLines.push({ type: 'subsection-header', content: line.substring(4) });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
        isInExecutiveSummary = false;
        isInSentimentOverview = false;
      }
      // Subsubsection headers (#### headers)
      else if (line.startsWith('#### ')) {
        processedLines.push({ type: 'subsubsection-header', content: line.substring(5) });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
        isInExecutiveSummary = false;
        isInSentimentOverview = false;
      }
      // Block quotes (> text)
      else if (line.startsWith('> ')) {
        const content = line.substring(2);
        // Check if it has italic markdown (*text*)
        const hasItalic = content.includes('*') && !content.includes('**');
        processedLines.push({ type: 'blockquote', content: content, hasItalic: hasItalic });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
      }
      // Numbered lists with colon (like "1. **Leadership Risk**:" becomes a numbered header)
      else if (/^\d+\.\s/.test(line) && line.includes(':')) {
        const match = line.match(/^(\d+)\.\s(.+)$/);
        if (match) {
          const number = match[1];
          const content = match[2];
          // If it ends with a colon, treat as numbered-header
          if (content.trim().endsWith(':')) {
            processedLines.push({ type: 'numbered-header', number: number, content: content.trim() });
            inBulletHeaderContext = true;
            inStandaloneHeaderContext = false;
          } else {
            // If colon is in the middle with content after, split it
            const colonIndex = content.indexOf(':');
            const header = content.substring(0, colonIndex + 1).trim();
            const text = content.substring(colonIndex + 1).trim();
            processedLines.push({ type: 'numbered-header', number: number, content: header });
            if (text) {
              const shouldBold = isEntireLineBold && (isInExecutiveSummary || isInSentimentOverview);
              processedLines.push({ type: 'sub-bullet', content: text, bold: shouldBold, underStandaloneHeader: false });
            }
            inBulletHeaderContext = true;
            inStandaloneHeaderContext = false;
          }
        }
      }
      // Regular numbered lists
      else if (/^\d+\.\s/.test(line)) {
        const match = line.match(/^(\d+)\.\s(.+)$/);
        if (match) {
          const number = match[1];
          const content = match[2];
          const shouldBold = isEntireLineBold && (isInExecutiveSummary || isInSentimentOverview);
          processedLines.push({ type: 'numbered-bullet', number: number, content: content, bold: shouldBold });
          inBulletHeaderContext = false;
        }
      }
      // Checkmark bullets (✓, ✅) - treat as special bullets
      // Use direct emoji matching which is safer for webpack/babel
      else if (/^[✓✅⚠️📈📉💡🎯🔍⚡☑️☐❌⭕🔴🟢🟡🟩🟧🟥🔵⬆️⬇️➡️⬅️🎉🔥💰📊📉📈⚡✨🌟⭐💪👍👎❤️💙💚💛🧡💜🖤🤍💔✔️❗❓⭕]/.test(line)) {
        // Extract the icon by finding where the actual content starts
        // Some emojis can be 1-2 characters, so we need to be careful
        let iconLength = 1;
        const firstChar = line.charAt(0);
        const secondChar = line.charAt(1);
        
        // Check if it's a two-character emoji (emoji with variation selector)
        if (secondChar && (secondChar === '\uFE0F' || (firstChar + secondChar).match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/))) {
          iconLength = 2;
        }
        
        const icon = line.substring(0, iconLength);
        const content = line.substring(iconLength).trim();
        processedLines.push({ type: 'icon-bullet', icon: icon, content: content });
        inBulletHeaderContext = false;
      }
      // Bullet points with colon (like "- **Key Metrics**:" becomes a sub-header)
      else if (line.startsWith('- ') && line.includes(':')) {
        const content = line.substring(2);
        // If it ends with a colon and has content, treat as bullet-header
        if (content.trim().endsWith(':')) {
          processedLines.push({ type: 'bullet-header', content: content.trim(), bold: false });
          inBulletHeaderContext = true;
          inStandaloneHeaderContext = false;
        } else {
          // If colon is in the middle with content after, split it
          const colonIndex = content.indexOf(':');
          const header = content.substring(0, colonIndex + 1).trim();
          const text = content.substring(colonIndex + 1).trim();
          processedLines.push({ type: 'bullet-header', content: header, bold: false });
          if (text) {
            const shouldBold = isEntireLineBold && (isInExecutiveSummary || isInSentimentOverview);
            processedLines.push({ type: 'sub-bullet', content: text, bold: shouldBold, underStandaloneHeader: false });
          }
          inBulletHeaderContext = true;
          inStandaloneHeaderContext = false;
        }
      }
      // Regular bullet points - check if we're in a bullet-header context
      else if (line.startsWith('- ')) {
        const shouldBold = isEntireLineBold && (isInExecutiveSummary || isInSentimentOverview);
        if (inBulletHeaderContext) {
          processedLines.push({ type: 'sub-bullet', content: line.substring(2), bold: shouldBold, underStandaloneHeader: inStandaloneHeaderContext });
        } else {
          processedLines.push({ type: 'bullet', content: line.substring(2), bold: shouldBold });
        }
      }
      // Indented content (treat as sub-bullet or sub-sub-bullet based on indentation)
      else if (line.startsWith('    ') && processedLines.length > 0) {
        // 4+ spaces = sub-sub-bullet
        const shouldBold = isEntireLineBold && (isInExecutiveSummary || isInSentimentOverview);
        processedLines.push({ type: 'sub-sub-bullet', content: line.trim(), bold: shouldBold, underStandaloneHeader: inStandaloneHeaderContext });
      }
      else if (line.startsWith('  ') && processedLines.length > 0) {
        // 2 spaces = sub-bullet
        const shouldBold = isEntireLineBold && (isInExecutiveSummary || isInSentimentOverview);
        processedLines.push({ type: 'sub-bullet', content: line.trim(), bold: shouldBold, underStandaloneHeader: inStandaloneHeaderContext });
      }
      // Standalone bold headers with colon (like "**Executive Summary:**" or "**Sentiment Overview:**")
      else if (hasBoldMarkers && line.includes(':') && line.trim().endsWith(':')) {
        processedLines.push({ type: 'standalone-header', content: line, bold: true });
        inBulletHeaderContext = true;
        inStandaloneHeaderContext = true;
        // Check if this is Executive Summary or Sentiment Overview
        const lowerContent = line.toLowerCase();
        isInExecutiveSummary = lowerContent.includes('executive summary');
        isInSentimentOverview = lowerContent.includes('sentiment overview');
      }
      // Horizontal rule
      else if (line === '---') {
        processedLines.push({ type: 'divider', content: '' });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
        // Reset section flags when hitting a divider
        isInExecutiveSummary = false;
        isInSentimentOverview = false;
      }
      // Regular text
      else {
        const shouldBold = isEntireLineBold && (isInExecutiveSummary || isInSentimentOverview);
        processedLines.push({ type: 'text', content: line, bold: shouldBold });
        inBulletHeaderContext = false;
        inStandaloneHeaderContext = false;
      }
    }
    
    // Flush any remaining table
    if (inTable || tableRows.length > 0) {
      if (tableHeaders.length > 0 && tableRows.length > 0) {
        processedLines.push({ type: 'table', headers: tableHeaders, rows: tableRows });
      } else if (tableRows.length > 0) {
        processedLines.push({ type: 'table', headers: tableRows[0], rows: tableRows.slice(1) });
      }
    }
    
    // Flush any remaining code block
    if (inCodeBlock && codeBlockContent.length > 0) {
      const isStructuredData = codeBlockContent.some(l =>
        l.includes('├─') || l.includes('└─') || l.includes('│') || /[↑↓]/.test(l)
      );
      processedLines.push({ type: isStructuredData ? 'structured-data' : 'code-block', content: codeBlockContent.join('\n') });
    }
    
    return processedLines;
  };
  
  // Helper function to render inline formatting (bold, italic, etc.)
  const renderInlineFormatting = (text) => {
    const parts = [];
    let currentIndex = 0;
    let key = 0;
    
    // Remove single asterisks for italics and keep bold
    const regex = /(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        parts.push(text.substring(currentIndex, match.index));
      }
      
      const matched = match[0];
      if (matched.startsWith('***') && matched.endsWith('***')) {
        // Bold and italic
        parts.push(
          <strong key={key++}><em>{matched.substring(3, matched.length - 3)}</em></strong>
        );
      } else if (matched.startsWith('**') && matched.endsWith('**')) {
        // Bold
        parts.push(<strong key={key++}>{matched.substring(2, matched.length - 2)}</strong>);
      } else if (matched.startsWith('*') && matched.endsWith('*')) {
        // Italic
        parts.push(<em key={key++}>{matched.substring(1, matched.length - 1)}</em>);
      }
      
      currentIndex = match.index + matched.length;
    }
    
    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  return (
    <div style={{ padding: '20px', background: '#ffffff', borderRadius: '8px', margin: '20px 0' }}>
      {summary.map((section, index) => {
        const processedLines = preprocessSection(section);
        return (
          <div key={index} style={{
              background: '#f9f9f9',
              padding: '20px',
              margin: '15px 0',
              borderRadius: '8px',
              borderLeft: '4px solid #007bff'
            }}>
            
            {processedLines.map((line, lineIndex) => {
              if (line.type === 'main-title') {
                return (
                  <h2 key={lineIndex} style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    marginBottom: '20px',
                    color: '#1a1a1a'
                  }}>
                    {line.content}
                  </h2>
                );
              } else if (line.type === 'section-header') {
                return (
                  <h3 key={lineIndex} style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginTop: '20px',
                    marginBottom: '10px',
                    color: '#2c2c2c',
                    borderBottom: '2px solid #007bff',
                    paddingBottom: '5px'
                  }}>
                    {line.content}
                  </h3>
                );
              } else if (line.type === 'subsection-header') {
                return (
                  <h4 key={lineIndex} style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginTop: '15px',
                    marginBottom: '8px',
                    color: '#444'
                  }}>
                    {line.content}
                  </h4>
                );
              } else if (line.type === 'subsubsection-header') {
                return (
                  <h5 key={lineIndex} style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginTop: '12px',
                    marginBottom: '6px',
                    color: '#555'
                  }}>
                    {line.content}
                  </h5>
                );
              } else if (line.type === 'blockquote') {
                return (
                  <blockquote key={lineIndex} style={{
                    borderLeft: '4px solid #ccc',
                    marginLeft: '20px',
                    paddingLeft: '15px',
                    marginTop: '15px',
                    marginBottom: '15px',
                    fontStyle: line.hasItalic ? 'italic' : 'normal',
                    color: '#666',
                    backgroundColor: '#f5f5f5',
                    padding: '10px 15px',
                    borderRadius: '4px'
                  }}>
                    {renderInlineFormatting(line.content)}
                  </blockquote>
                );
              } else if (line.type === 'code-block') {
                return (
                  <pre key={lineIndex} style={{
                    backgroundColor: '#2d2d2d',
                    color: '#f8f8f2',
                    padding: '15px',
                    borderRadius: '6px',
                    overflowX: 'auto',
                    marginTop: '15px',
                    marginBottom: '15px',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                  }}>
                    <code>{line.content}</code>
                  </pre>
                );
              } else if (line.type === 'structured-data') {
                const sdLines = line.content.split('\n');
                const hasTree = sdLines.some(l => l.includes('├─') || l.includes('└─'));
                const hasMetricPattern = !hasTree && sdLines.some(l => /:\s+[↑↓]/.test(l));

                if (hasMetricPattern) {
                  // Metrics dashboard: "Label:   ↑/↓ (value)"
                  return (
                    <div key={lineIndex} style={{ marginTop: '15px', marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {sdLines.filter(l => l.trim()).map((mLine, mIdx) => {
                        const arrowMatch = mLine.match(/^(.+?):\s+([↑↓])\s*(.+)?$/);
                        if (arrowMatch) {
                          const [, label, arrow, value] = arrowMatch;
                          const isUp = arrow === '↑';
                          return (
                            <div key={mIdx} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '9px 14px', backgroundColor: '#fff', borderRadius: '6px',
                              border: '1px solid #e8ecf0',
                              borderLeft: `4px solid ${isUp ? '#28a745' : '#dc3545'}`
                            }}>
                              <span style={{ color: '#444', fontWeight: '500', fontSize: '14px' }}>{label.trim()}</span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: isUp ? '#28a745' : '#dc3545', fontWeight: '600', fontSize: '14px' }}>
                                <span style={{ fontSize: '17px', lineHeight: 1 }}>{arrow}</span>
                                {value && <span>{value.trim()}</span>}
                              </span>
                            </div>
                          );
                        }
                        return (
                          <div key={mIdx} style={{ color: '#666', fontSize: '13px', padding: '2px 14px' }}>
                            {mLine}
                          </div>
                        );
                      })}
                    </div>
                  );
                } else if (hasTree) {
                  // Decision trees: preserve branch depth and group each ranked driver.
                  // Connector-only lines are layout guides in the raw text, not content.
                  let treeTitle = '';
                  const drivers = [];
                  let currentDriver = null;
                  let lastScenarioDepth = 0;

                  for (const sdLine of sdLines) {
                    const trimmedLine = sdLine.trim();
                    if (!trimmedLine || /^[│\s]+$/.test(sdLine)) continue;

                    const branchMatch = sdLine.match(/^([│\s]*)([├└])─+─?\s*(.+)$/);
                    if (!branchMatch) {
                      if (!treeTitle) treeTitle = trimmedLine;
                      continue;
                    }

                    const [, prefix, connector, rawContent] = branchMatch;
                    const depth = (prefix.match(/│/g) || []).length;
                    const content = rawContent.trim();
                    const priorityMatch = depth === 0 && content.match(/^\[([^\]]+)\]\s*(.+)$/);

                    if (depth === 0) {
                      currentDriver = {
                        priority: priorityMatch ? priorityMatch[1].toUpperCase() : null,
                        title: priorityMatch ? priorityMatch[2] : content,
                        connector,
                        items: []
                      };
                      drivers.push(currentDriver);
                      lastScenarioDepth = 0;
                      continue;
                    }

                    if (!currentDriver) continue;
                    const isOutcome = content.startsWith('→');
                    const displayDepth = isOutcome
                      ? Math.max(depth, lastScenarioDepth + 1)
                      : depth;

                    if (!isOutcome) lastScenarioDepth = depth;
                    currentDriver.items.push({ content, depth: displayDepth, isOutcome, connector });
                  }

                  const priorityStyles = {
                    CRITICAL: { color: '#b91c1c', background: '#fef2f2', border: '#fecaca' },
                    HIGH: { color: '#b45309', background: '#fffbeb', border: '#fde68a' },
                    MEDIUM: { color: '#0369a1', background: '#f0f9ff', border: '#bae6fd' },
                    LOW: { color: '#047857', background: '#ecfdf5', border: '#a7f3d0' }
                  };

                  const getOutcomeStyle = (content) => {
                    const lower = content.toLowerCase();
                    if (lower.includes('bearish') || lower.includes('weakens')) {
                      return { color: '#b91c1c', background: '#fef2f2', border: '#fca5a5' };
                    }
                    if (lower.includes('ambiguous') || lower.includes('mixed')) {
                      return { color: '#a16207', background: '#fffbeb', border: '#fcd34d' };
                    }
                    return { color: '#15803d', background: '#f0fdf4', border: '#86efac' };
                  };

                  return (
                    <div key={lineIndex} style={{
                      margin: '18px 0',
                      border: '1px solid #bfdbfe',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      backgroundColor: '#f8fafc',
                      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)'
                    }}>
                      {treeTitle && (
                        <div style={{
                          background: 'linear-gradient(135deg, #0d6efd 0%, #0756b8 100%)',
                          color: '#fff',
                          padding: '13px 18px',
                          fontWeight: '700',
                          fontSize: '17px',
                          letterSpacing: '0.1px'
                        }}>
                          {treeTitle.replace(/:$/, '')}
                        </div>
                      )}
                      <div style={{ padding: '14px', display: 'grid', gap: '12px' }}>
                        {drivers.map((driver, driverIndex) => {
                          const badgeStyle = priorityStyles[driver.priority] || priorityStyles.MEDIUM;
                          return (
                            <section key={driverIndex} style={{
                              backgroundColor: '#fff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '9px',
                              overflow: 'hidden',
                              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '11px 14px',
                                backgroundColor: badgeStyle.background,
                                borderBottom: `1px solid ${badgeStyle.border}`
                              }}>
                                {driver.priority && (
                                  <span style={{
                                    flexShrink: 0,
                                    padding: '3px 8px',
                                    borderRadius: '999px',
                                    border: `1px solid ${badgeStyle.border}`,
                                    color: badgeStyle.color,
                                    backgroundColor: '#fff',
                                    fontSize: '11px',
                                    fontWeight: '800',
                                    letterSpacing: '0.5px'
                                  }}>
                                    {driver.priority}
                                  </span>
                                )}
                                <span style={{ color: '#1e293b', fontWeight: '700', fontSize: '15px', lineHeight: '1.4' }}>
                                  {renderInlineFormatting(driver.title)}
                                </span>
                              </div>
                              <div style={{ padding: '8px 12px 10px' }}>
                                {driver.items.map((item, itemIndex) => {
                                  if (item.isOutcome) {
                                    const outcomeStyle = getOutcomeStyle(item.content);
                                    const outcomeContent = item.content.replace(/^→\s*/, '');
                                    return (
                                      <div key={itemIndex} style={{
                                        margin: '6px 0',
                                        marginLeft: `${Math.min(item.depth, 3) * 18}px`,
                                        padding: '8px 11px',
                                        borderLeft: `3px solid ${outcomeStyle.border}`,
                                        borderRadius: '0 6px 6px 0',
                                        backgroundColor: outcomeStyle.background,
                                        color: outcomeStyle.color,
                                        fontSize: '14px',
                                        lineHeight: '1.5'
                                      }}>
                                        <span style={{ fontWeight: '800', marginRight: '7px' }}>→</span>
                                        {renderInlineFormatting(outcomeContent)}
                                      </div>
                                    );
                                  }

                                  return (
                                    <div key={itemIndex} style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '9px',
                                      marginLeft: `${Math.max(0, item.depth - 1) * 18}px`,
                                      padding: '7px 6px',
                                      color: '#334155',
                                      fontSize: '14px',
                                      lineHeight: '1.5'
                                    }}>
                                      <span style={{
                                        color: item.connector === '└' ? '#22c55e' : '#3b82f6',
                                        fontSize: '9px',
                                        lineHeight: '21px',
                                        flexShrink: 0
                                      }}>●</span>
                                      <span>{renderInlineFormatting(item.content)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </section>
                          );
                        })}
                      </div>
                    </div>
                  );
                } else {
                  // Plain comparison / mixed data
                  const nonEmpty = sdLines.filter(l => l.trim());
                  const titleLine = nonEmpty[0]?.trim();
                  const dataLines = nonEmpty.slice(1);
                  return (
                    <div key={lineIndex} style={{ marginTop: '15px', marginBottom: '15px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #d0d8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      {titleLine && (
                        <div style={{ background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)', color: '#fff', padding: '9px 14px', fontWeight: '600', fontSize: '14px' }}>
                          {titleLine.replace(/:$/, '')}
                        </div>
                      )}
                      <div style={{ backgroundColor: '#f8f9fa', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {dataLines.map((dLine, dIdx) => (
                          <div key={dIdx} style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: '13px', color: '#333', lineHeight: '1.8' }}>
                            {dLine}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
              } else if (line.type === 'table') {
                return (
                  <div key={lineIndex} style={{
                    overflowX: 'auto',
                    marginTop: '15px',
                    marginBottom: '15px'
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#007bff', color: '#fff' }}>
                          {line.headers.map((header, hIdx) => (
                            <th key={hIdx} style={{
                              padding: '12px',
                              textAlign: 'left',
                              fontWeight: '600',
                              borderBottom: '2px solid #0056b3'
                            }}>
                              {renderInlineFormatting(header)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {line.rows.map((row, rIdx) => (
                          <tr key={rIdx} style={{
                            backgroundColor: rIdx % 2 === 0 ? '#f9f9f9' : '#fff',
                            borderBottom: '1px solid #e0e0e0'
                          }}>
                            {row.map((cell, cIdx) => (
                              <td key={cIdx} style={{
                                padding: '10px 12px',
                                color: '#333'
                              }}>
                                {renderInlineFormatting(cell)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              } else if (line.type === 'icon-bullet') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '20px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <span style={{ 
                      marginRight: '10px', 
                      fontSize: '18px',
                      minWidth: '25px'
                    }}>{line.icon}</span>
                    <span style={{ flex: 1 }}>{renderInlineFormatting(line.content)}</span>
                  </div>
                );
              } else if (line.type === 'numbered-header') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '20px',
                    marginTop: '12px',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    <span style={{ marginRight: '10px', color: '#007bff', minWidth: '25px' }}>{line.number}.</span>
                    <span style={{ flex: 1 }}>{renderInlineFormatting(line.content)}</span>
                  </div>
                );
              } else if (line.type === 'numbered-bullet') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '20px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <span style={{ marginRight: '10px', color: '#007bff', minWidth: '25px', fontWeight: line.bold ? '600' : 'normal' }}>{line.number}.</span>
                    <span style={{ flex: 1, fontWeight: line.bold ? '600' : 'normal' }}>{renderInlineFormatting(line.content)}</span>
                  </div>
                );
              } else if (line.type === 'standalone-header') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '0px',
                    marginTop: '15px',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    {renderInlineFormatting(line.content)}
                  </div>
                );
              } else if (line.type === 'bullet-header') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '20px',
                    marginTop: '12px',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    fontWeight: line.bold ? '600' : 'normal',
                    color: '#333'
                  }}>
                    <span style={{ marginRight: '10px', color: '#007bff' }}>•</span>
                    <span style={{ flex: 1 }}>{renderInlineFormatting(line.content)}</span>
                  </div>
                );
              } else if (line.type === 'sub-bullet') {
                // Use less indent if under standalone header, more if under bullet-header
                const leftMargin = line.underStandaloneHeader ? '20px' : '55px';
                // Blue bullets for 20px indent (same as regular bullets), grey for 45px
                const bulletColor = line.underStandaloneHeader ? '#007bff' : '#999';
                // Make blue bullets bigger
                const bulletSize = line.underStandaloneHeader ? '10px' : '8px';
                const bulletMarginTop = line.underStandaloneHeader ? '5px' : '6px';
                return (
                  <div key={lineIndex} style={{
                    marginLeft: leftMargin,
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    color: line.bold ? '#333' : '#555',
                    lineHeight: '1.6',
                    fontWeight: line.bold ? '600' : 'normal'
                  }}>
                    <span style={{ 
                      marginRight: '10px', 
                      color: bulletColor,
                      fontSize: bulletSize,
                      marginTop: bulletMarginTop
                    }}>●</span>
                    <span style={{ flex: 1 }}>{renderInlineFormatting(line.content)}</span>
                  </div>
                );
              } else if (line.type === 'sub-sub-bullet') {
                // Use less indent if under standalone header, more if under bullet-header
                const leftMargin = line.underStandaloneHeader ? '45px' : '80px';
                // Grey bullets for deeper indentation
                const bulletColor = '#ccc';
                return (
                  <div key={lineIndex} style={{
                    marginLeft: leftMargin,
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    color: line.bold ? '#444' : '#666',
                    lineHeight: '1.5',
                    fontSize: '14px',
                    fontWeight: line.bold ? '600' : 'normal'
                  }}>
                    <span style={{ 
                      marginRight: '8px', 
                      color: bulletColor,
                      fontSize: '6px',
                      marginTop: '7px'
                    }}>●</span>
                    <span style={{ flex: 1 }}>{renderInlineFormatting(line.content)}</span>
                  </div>
                );
              } else if (line.type === 'bullet') {
                return (
                  <div key={lineIndex} style={{
                    marginLeft: '20px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}>
                    <span style={{ marginRight: '10px', color: '#007bff' }}>•</span>
                    <span style={{ flex: 1, fontWeight: line.bold ? '600' : 'normal' }}>{renderInlineFormatting(line.content)}</span>
                  </div>
                );
              } else if (line.type === 'divider') {
                return (
                  <hr key={lineIndex} style={{
                    border: 'none',
                    borderTop: '1px solid #ddd',
                    margin: '20px 0'
                  }} />
                );
              } else if (line.type === 'text') {
                return (
                  <p key={lineIndex} style={{
                    marginBottom: '10px',
                    lineHeight: '1.6',
                    color: '#555',
                    fontWeight: line.bold ? '600' : 'normal'
                  }}>
                    {renderInlineFormatting(line.content)}
                  </p>
                );
              }
              return null;
            })}
          </div>
        );
      })}
    </div>
  );
};

export default SummaryDisplay;
